export class HeaderComponent extends HTMLElement {
  constructor() {
    super()
    this.activeMenu = null
    /** @type {number | null} */
    this.hoverTimeout = null
    this.menuMap = new Map()
    this.menuPanel = null
    this.headerInner = null
    this.isHomepage = false
    this.scrollThreshold = window.innerHeight - 76 // 100vh
    this.isScrolled = false
    /** @type {((event: Event) => void) | null} */
    this.boundHandleScroll = null
    this.initialized = false
  }

  connectedCallback() {
    // Initialize menu hover immediately
    this.initializeMenuHover()

    // Set initial state immediately to prevent flashing
    this.setInitialState()

    // Wait for DOM to be ready before full initialization
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.initializeHeaderStyling()
      })
    } else {
      this.initializeHeaderStyling()
    }
  }

  /**
   * Set initial state immediately to prevent flashing
   */
  setInitialState() {
    this.headerInner = /** @type {HTMLElement} */ (
      this.querySelector('.header-inner')
    )
    if (!this.headerInner) return

    // Check if we're on the homepage using data attribute
    const templateName = this.getAttribute('data-template')
    this.isHomepage = templateName === 'index'

    // Set styles directly via JavaScript to prevent any flash
    if (this.isHomepage) {
      // Homepage: start with transparent background and white text
      this.headerInner.style.backgroundColor = 'transparent'
      this.headerInner.style.color = 'white'
      // Set menu button colors
      const menuButtons = this.headerInner.querySelectorAll('.menu-button, a')
      menuButtons.forEach((button) => {
        /** @type {HTMLElement} */ button.style.color = 'white'
      })
      this.headerInner.classList.add('homepage-transparent')
    } else {
      // Other pages: white background and black text
      this.headerInner.style.backgroundColor = 'white'
      this.headerInner.style.color = 'black'
      // Set menu button colors
      const menuButtons = this.headerInner.querySelectorAll('.menu-button, a')
      menuButtons.forEach((button) => {
        /** @type {HTMLElement} */ button.style.color = 'black'
      })
      this.headerInner.classList.add('other-page')
    }
  }

  initializeMenuHover() {
    const primaryMenuButtons = this.querySelectorAll('[data-menu-button]')
    const menuModals = this.querySelectorAll('[data-menu-modal]')
    this.menuPanel = this.querySelector('#menu-panel')
    this.headerInner = /** @type {HTMLElement} */ (
      this.querySelector('.header-inner')
    )

    // Create a map of menu button to modal for quick lookup
    primaryMenuButtons.forEach((button) => {
      const menuHandle = button.getAttribute('data-menu-button')
      const modal = this.querySelector(`[data-menu-modal="${menuHandle}"]`)
      if (modal) {
        this.menuMap.set(button, modal)
      }
    })

    // Add hover event listeners
    primaryMenuButtons.forEach((button) => {
      button.addEventListener('mouseenter', () => {
        // If this button has a corresponding menu modal, show it
        if (this.menuMap.has(button)) {
          this.showMenu(/** @type {HTMLElement} */ (button))
        } else {
          // If it doesn't have a modal, close any active menus
          this.hideAllMenus()
        }
      })
    })

    // Top-level menu links without panels (secondary menu, loyalty, etc.)
    if (this.headerInner) {
      const nonPanelTopLinks = this.headerInner.querySelectorAll(
        '.menu-button:not([data-menu-button])'
      )

      nonPanelTopLinks.forEach((link) => {
        link.addEventListener('mouseenter', () => {
          this.hideAllMenus()
        })
      })
    }

    // Add hover event listeners to modals to keep them open
    menuModals.forEach((modal) => {
      modal.addEventListener('mouseenter', () =>
        this.keepMenuOpen(/** @type {HTMLElement} */ (modal))
      )
    })

    // Hide all menus when leaving the header component
    this.addEventListener('mouseleave', (e) => {
      // Only close if we're actually leaving the header component
      // (not just moving between child elements)
      if (!this.contains(/** @type {Node} */ (e.relatedTarget))) {
        this.hideAllMenus()
      }
    })
  }

  /**
   * Show menu modal on hover
   * @param {HTMLElement} button - The menu button element
   */
  showMenu(button) {
    // Clear any existing timeout
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
      this.hoverTimeout = null
    }

    const modal = this.menuMap.get(button)

    if (modal && modal !== this.activeMenu) {
      // Hide current active menu and remove active class from its button
      if (this.activeMenu) {
        this.activeMenu.classList.remove('active')
        // Find and remove active class from the corresponding button
        const activeButton = this.findButtonForModal(this.activeMenu)
        if (activeButton) {
          activeButton.classList.remove('active')
        }
      }

      // Show new menu and add active class to its button
      this.activeMenu = modal
      modal.classList.add('active')
      button.classList.add('active')

      // Open the panel and animate height
      this.openMenuPanel()
    }
  }

  /**
   * Hide menu modal
   * @param {HTMLElement} element - The menu button or modal element
   */
  hideMenu(element) {
    // Don't close menu when leaving individual elements
    // Only close when leaving the entire header component
    // This is handled by the mouseleave event on the header component
  }

  /**
   * Keep menu open when hovering over modal
   * @param {HTMLElement} modal - The menu modal element
   */
  keepMenuOpen(modal) {
    // Clear timeout when hovering over modal
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout)
      this.hoverTimeout = null
    }
  }

  /**
   * Check if mouse is hovering over any menu button
   * @returns {boolean} - True if hovering over a menu button
   */
  isHoveringMenuButton() {
    const menuButtons = this.querySelectorAll('[data-menu-button]')
    return Array.from(menuButtons).some((button) => button.matches(':hover'))
  }

  hideAllMenus() {
    const menuModals = this.querySelectorAll('[data-menu-modal]')
    menuModals.forEach((modal) => {
      modal.classList.remove('active')
    })

    // Remove active class from all menu buttons
    const menuButtons = this.querySelectorAll('[data-menu-button]')
    menuButtons.forEach((button) => {
      button.classList.remove('active')
    })

    // Fully reset menu state when leaving the header
    this.closeMenuPanel()
    this.activeMenu = null
  }

  /**
   * Find the button that corresponds to a given modal
   * @param {HTMLElement} modal - The menu modal element
   * @returns {HTMLElement | null} - The corresponding button element
   */
  findButtonForModal(modal) {
    const menuHandle = modal.getAttribute('data-menu-modal')
    return this.querySelector(`[data-menu-button="${menuHandle}"]`)
  }

  /**
   * Initialize header styling based on page type and scroll position
   */
  initializeHeaderStyling() {
    // Prevent multiple initializations
    if (this.initialized) return

    // Page type is already set in setInitialState, just update styling
    this.updateHeaderStyling()

    // Add scroll listener for homepage
    if (this.isHomepage) {
      // Bind the scroll handler once to avoid creating new functions
      this.boundHandleScroll = this.handleScroll.bind(this)
      if (this.boundHandleScroll) {
        window.addEventListener('scroll', this.boundHandleScroll)
      }
      // Check initial scroll position after a small delay to ensure page is stable
      setTimeout(() => {
        this.handleScroll()
      }, 100)
    }

    this.initialized = true
  }

  /**
   * Handle scroll events for homepage header styling
   */
  handleScroll() {
    if (!this.isHomepage) return

    const scrollY = window.scrollY
    const wasScrolled = this.isScrolled
    this.isScrolled = scrollY >= this.scrollThreshold

    // Only update if scroll state changed
    if (wasScrolled !== this.isScrolled) {
      this.updateHeaderStyling()
    }
  }

  /**
   * Update header styling based on current state
   */
  updateHeaderStyling() {
    if (!this.headerInner) return

    // Remove all styling classes
    this.headerInner.classList.remove(
      'homepage-transparent',
      'homepage-scrolled',
      'other-page',
      'menu-open-cream'
    )

    if (this.isHomepage) {
      if (this.isScrolled) {
        // Homepage scrolled: white background, black text
        this.headerInner.style.backgroundColor = 'white'
        this.headerInner.style.color = 'black'
        const menuButtons = this.headerInner.querySelectorAll('.menu-button, a')
        menuButtons.forEach((button) => {
          /** @type {HTMLElement} */ button.style.color = 'black'
        })
        this.headerInner.classList.add('homepage-scrolled')
      } else {
        // Homepage not scrolled: transparent background, white text
        this.headerInner.style.backgroundColor = 'transparent'
        this.headerInner.style.color = 'white'
        const menuButtons = this.headerInner.querySelectorAll('.menu-button, a')
        menuButtons.forEach((button) => {
          /** @type {HTMLElement} */ button.style.color = 'white'
        })
        this.headerInner.classList.add('homepage-transparent')
      }
    } else {
      // Other pages: white background, black text
      this.headerInner.style.backgroundColor = 'white'
      this.headerInner.style.color = 'black'
      const menuButtons = this.headerInner.querySelectorAll('.menu-button, a')
      menuButtons.forEach((button) => {
        /** @type {HTMLElement} */ button.style.color = 'black'
      })
      this.headerInner.classList.add('other-page')
    }
  }

  /**
   * Override openMenuPanel to add cream background when menu is open
   */
  openMenuPanel() {
    if (this.menuPanel) {
      this.menuPanel.classList.add('open')
      if (this.headerInner) {
        this.headerInner.classList.add('menu-active')
        // Add cream background when menu is open
        this.headerInner.style.backgroundColor = '#efeddf'
        // Change text to black for better contrast against cream background
        this.headerInner.style.color = 'black'
        const menuButtons = this.headerInner.querySelectorAll('.menu-button, a')
        menuButtons.forEach((button) => {
          /** @type {HTMLElement} */ button.style.color = 'black'
        })
        this.headerInner.classList.add('menu-open-cream')
      }
    }
  }

  /**
   * Override closeMenuPanel to remove cream background when menu is closed
   */
  closeMenuPanel() {
    if (this.menuPanel) {
      this.menuPanel.classList.remove('open')
      if (this.headerInner) {
        this.headerInner.classList.remove('menu-active')
        // Remove cream background when menu is closed
        this.headerInner.classList.remove('menu-open-cream')
        // Reapply correct styling based on current state
        this.updateHeaderStyling()
      }
    }
  }

  /**
   * Clean up event listeners when component is disconnected
   */
  disconnectedCallback() {
    if (this.isHomepage && this.boundHandleScroll) {
      window.removeEventListener('scroll', this.boundHandleScroll)
    }
  }
}

customElements.define('header-component', HeaderComponent)
